import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { COLORS } from "../../../const/colors";

const TooltipContainer = styled(motion.div)`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 400px;
  background-color: ${(props) =>
    props.light ? COLORS.DARK_BLUE_80 : COLORS.WHITE_80};
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  z-index: 99999;
  backdrop-filter: blur(8px);
  border: 1px solid ${COLORS.ORANGE_20};
  pointer-events: auto;
  will-change: transform;

  &::before {
    content: "";
    position: absolute;
    top: -8px;
    left: ${(props) => props.arrowPosition || "50%"};
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid
      ${(props) => (props.light ? COLORS.DARK_BLUE_80 : COLORS.WHITE_80)};
    filter: drop-shadow(0 -1px 1px rgba(0, 0, 0, 0.1));
  }

  @media (max-width: 768px) {
    left: 0;
    transform: none;
    min-width: 250px;

    &::before {
      left: 20px;
      transform: none;
    }
  }
`;

const TooltipTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};
  border-bottom: 1px solid ${COLORS.ORANGE_20};
  padding-bottom: 0.5rem;
`;

const SkillDescription = styled.p`
  font-size: 0.875rem;
  color: ${(props) => (props.light ? COLORS.WHITE_80 : COLORS.BLACK_80)};
  margin-bottom: 1rem;
  line-height: 1.4;
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed ${COLORS.ORANGE_30};
`;

const RelationshipStats = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  background-color: ${(props) =>
    props.light ? COLORS.WHITE_10 : COLORS.ORANGE_10};
  color: ${(props) => (props.light ? COLORS.WHITE_90 : COLORS.BLACK_70)};
`;

const EmptyMessage = styled.p`
  font-size: 0.875rem;
  color: ${(props) => (props.light ? COLORS.WHITE_70 : COLORS.BLACK_70)};
  text-align: center;
  padding: 1rem 0;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) =>
    props.light ? COLORS.DARK_BLUE_30 : COLORS.WHITE_80};
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 0.75rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-3px);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
`;

const CardContent = styled.div`
  padding: 0.75rem;
`;

const CardTitle = styled.h5`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};
`;

const CardLink = styled.a`
  display: inline-block;
  font-size: 0.75rem;
  color: ${COLORS.ORANGE};
  text-decoration: none;
  margin-top: 0.5rem;
  transition: color 0.2s ease;

  &:hover {
    color: ${COLORS.DARK_BLUE};
    text-decoration: underline;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.5rem;
`;

const PaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.disabled ? COLORS.ORANGE_30 : COLORS.ORANGE_80};
  color: ${COLORS.WHITE};
  border: none;
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px ${COLORS.ORANGE_20};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${COLORS.WHITE_30},
      transparent
    );
    transition: all 0.6s ease;
  }

  &:hover:not(:disabled) {
    background-color: ${COLORS.ORANGE};
    transform: translateY(-2px);
    box-shadow: 0 3px 8px ${COLORS.ORANGE_30};

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px ${COLORS.ORANGE_30};
  }
`;

const PaginationInfo = styled.span`
  font-size: 0.75rem;
  color: ${(props) => (props.light ? COLORS.WHITE_70 : COLORS.BLACK_70)};
  margin: 0 0.5rem;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${COLORS.ORANGE_20};
  margin-bottom: 1rem;
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  background: none;
  border: none;
  border-bottom: 2px solid
    ${(props) => (props.active ? COLORS.ORANGE : "transparent")};
  color: ${(props) => {
    if (props.light) {
      return props.active ? COLORS.WHITE : COLORS.WHITE_70;
    } else {
      return props.active ? COLORS.BLACK : COLORS.BLACK_70;
    }
  }};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${COLORS.WHITE_30},
      transparent
    );
    transition: all 0.6s ease;
  }

  &:hover {
    color: ${(props) => (props.light ? COLORS.WHITE : COLORS.BLACK)};

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const SkillTooltip = ({
  skillItem,
  relatedProjects,
  relatedCertifications,
  darkMode,
}) => {
  const [activeTab, setActiveTab] = useState("projects");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [arrowPosition, setArrowPosition] = useState("50%");
  const tooltipRef = useRef(null);

  const currentData =
    activeTab === "projects" ? relatedProjects : relatedCertifications;
  const totalItems = currentData.length;

  // Reset current index when changing tabs
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeTab]);

  // Calculate arrow position to align with the center of the skill item
  useEffect(() => {
    if (tooltipRef.current) {
      // Force tooltip to be on top of everything
      tooltipRef.current.style.zIndex = 9999;

      // Check if tooltip would be cut off at screen edge
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // If tooltip would overflow right edge of viewport
      if (tooltipRect.right > viewportWidth) {
        const overflow = tooltipRect.right - viewportWidth;
        tooltipRef.current.style.transform = `translateX(calc(-50% - ${
          overflow + 20
        }px))`;
        setArrowPosition(`calc(50% + ${overflow + 20}px)`);
      }
      // If tooltip would overflow left edge of viewport
      else if (tooltipRect.left < 0) {
        const overflow = Math.abs(tooltipRect.left);
        tooltipRef.current.style.transform = `translateX(calc(-50% + ${
          overflow + 20
        }px))`;
        setArrowPosition(`calc(50% - ${overflow + 20}px)`);
      }
    }
  }, []);

  const handleNext = () => {
    if (currentIndex < totalItems - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
  };

  const cardVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
  };

  return (
    <TooltipContainer
      ref={tooltipRef}
      light={darkMode ? 1 : 0}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
      arrowPosition={arrowPosition}
      className="skill-tooltip"
    >
      {skillItem && skillItem.description && (
        <SkillDescription light={darkMode ? 1 : 0}>
          {skillItem.description}
        </SkillDescription>
      )}

      <TabContainer>
        <Tab
          active={activeTab === "projects"}
          light={darkMode ? 1 : 0}
          onClick={() => setActiveTab("projects")}
        >
          Projects ({relatedProjects.length})
        </Tab>
        <Tab
          active={activeTab === "certifications"}
          light={darkMode ? 1 : 0}
          onClick={() => setActiveTab("certifications")}
        >
          Certifications ({relatedCertifications.length})
        </Tab>
      </TabContainer>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab + currentIndex}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={cardVariants}
        >
          {currentData.length > 0 ? (
            <>
              {currentData[currentIndex] && (
                <Card light={darkMode ? 1 : 0}>
                  {currentData[currentIndex].image && (
                    <CardImage
                      src={currentData[currentIndex].image}
                      alt={currentData[currentIndex].title}
                    />
                  )}
                  <CardContent>
                    <CardTitle light={darkMode ? 1 : 0}>
                      {currentData[currentIndex].title}
                    </CardTitle>
                    {currentData[currentIndex].link && (
                      <CardLink
                        href={currentData[currentIndex].link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View{" "}
                        {activeTab === "projects" ? "Project" : "Certificate"}{" "}
                        <i className="bx bx-link-external"></i>
                      </CardLink>
                    )}
                  </CardContent>
                </Card>
              )}

              {totalItems > 1 && (
                <PaginationControls>
                  <PaginationButton
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                  >
                    <i className="bx bx-chevron-left"></i>
                  </PaginationButton>
                  <PaginationInfo light={darkMode ? 1 : 0}>
                    {currentIndex + 1} / {totalItems}
                  </PaginationInfo>
                  <PaginationButton
                    onClick={handleNext}
                    disabled={currentIndex === totalItems - 1}
                  >
                    <i className="bx bx-chevron-right"></i>
                  </PaginationButton>
                </PaginationControls>
              )}
            </>
          ) : (
            <EmptyMessage light={darkMode ? 1 : 0}>
              No {activeTab} related to this skill
            </EmptyMessage>
          )}
        </motion.div>
      </AnimatePresence>
    </TooltipContainer>
  );
};

export default SkillTooltip;
